import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileText } from "lucide-react";
import { Reveal } from "@/components/site/Motion";
import { journals, site } from "@/content/site";

const Arrow = () => (
  <span className="w-10 h-10 rounded-full border border-current grid place-items-center transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="w-4 h-4" /></span>
);

const JournalTile = ({ j, big }) => (
  <motion.article whileHover={{ y: -4 }} transition={{ duration: 0.4 }} className={`group relative bg-white border border-[var(--line)] flex ${big ? "flex-col sm:flex-row" : "flex-col"} h-full`} data-testid={`bento-journal-${j.slug}`}>
    <Link to={`/index.php/${j.slug}`} className={`relative overflow-hidden bg-[var(--paper-2)] ${big ? "sm:w-[42%] aspect-[2/3] sm:aspect-auto" : "aspect-[4/3]"}`} aria-label={j.title}>
      <img src={j.cover} alt={`${j.title} cover`} loading="lazy" className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.04] ${big ? "" : "object-top"}`} />
    </Link>
    <div className="flex-1 p-7 lg:p-9 flex flex-col">
      <p className="eyebrow mb-4">{j.abbr} · Journal</p>
      <h3 className={`font-display leading-[1.05] ${big ? "text-4xl lg:text-5xl" : "text-3xl"}`}>
        <Link to={`/index.php/${j.slug}`} className="link-u">{j.title}</Link>
      </h3>
      <p className="mt-5 text-[var(--ink-muted)] leading-relaxed line-clamp-4">{j.excerpt}</p>
      <div className="mt-auto pt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-medium">
        <Link to={`/index.php/${j.slug}`} className="inline-flex items-center gap-3 text-[var(--emerald)]" data-testid={`view-journal-${j.slug}`}>View Journal <Arrow /></Link>
        <Link to={`/index.php/${j.slug}/issue/current`} className="link-u" data-testid={`current-issue-${j.slug}`}>Current Issue</Link>
      </div>
    </div>
  </motion.article>
);

const Discipline = ({ d, i }) => (
  <div className="group relative overflow-hidden h-full min-h-[260px] bg-[var(--ink)]" data-testid={`bento-discipline-${i}`}>
    <img src={d.image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-[1.4s] group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
    <div className="relative h-full p-7 flex flex-col justify-end text-white">
      <p className="font-mono-x text-[11px] tracking-[0.2em] uppercase text-[#EAD9A8] mb-3">0{i + 1} · Our journals</p>
      <h3 className="font-display text-[1.75rem] leading-[1.1]">{d.name}</h3>
    </div>
  </div>
);

export const JournalsBento = () => (
  <section id="journals" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 scroll-mt-28" data-testid="journals-section">
    <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
      <Reveal className="lg:col-span-7">
        <p className="eyebrow mb-5">Journals</p>
        <h2 className="font-display text-5xl sm:text-6xl leading-[1.02]">Our journals, <em>in one reading room.</em></h2>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9">
        <p className="text-[var(--ink-muted)] text-lg leading-relaxed">{site.explore}</p>
      </Reveal>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5 lg:auto-rows-[minmax(280px,auto)]">
      <Reveal className="md:col-span-6 lg:col-span-8 lg:row-span-2"><JournalTile j={journals[0]} big /></Reveal>
      <Reveal delay={0.08} className="md:col-span-3 lg:col-span-4 lg:row-span-2"><JournalTile j={journals[1]} /></Reveal>
      {site.ourJournals.map((d, i) => (
        <Reveal key={d.name} delay={0.08 * i} className="md:col-span-3 lg:col-span-4"><Discipline d={d} i={i} /></Reveal>
      ))}
      <Reveal className="md:col-span-6 lg:col-span-12">
        <div className="bg-[var(--paper-2)] border border-[var(--line)] p-7 lg:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between" data-testid="bento-template-cta">
          <div className="flex items-center gap-5">
            <span className="w-14 h-14 shrink-0 rounded-full bg-[var(--emerald)] text-[var(--paper)] grid place-items-center"><FileText className="w-6 h-6" /></span>
            <div>
              <p className="font-display text-3xl">Preparing a manuscript?</p>
              <p className="text-[var(--ink-muted)]">Download the English Word template before you submit.</p>
            </div>
          </div>
          <a href={site.template.href} className="inline-flex items-center justify-center h-12 px-6 rounded-full border border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors" data-testid="bento-template-download">Download template (.docx)</a>
        </div>
      </Reveal>
    </div>
  </section>
);
